import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Attachment,
  ChatConfig,
  FeedbackPayload,
  FeedbackVote,
  Message,
  TransportRequest,
} from '../types';
import { resolveTransport } from '../transports';
import { describeHttpError } from '../transports/http';
import {
  fileToBase64,
  isAbortError,
  resolveConfig,
  resolveHeaders,
  serialiseAttachment,
  storage,
  toError,
  uid,
} from '../utils';

const FLUSH_INTERVAL_MS = 60;

export interface UseChatOptions {
  config: ChatConfig;
  persist?: boolean;
  storageKey?: string;
  welcomeMessage?: string;
  errorMessage?: string;
  onSend?: (message: Message) => void;
  onResponse?: (message: Message) => void;
  onFeedback?: (payload: FeedbackPayload) => void;
  onError?: (error: Error) => void;
  onConversationChange?: (conversationId: string, messages: Message[]) => void;
}

interface PersistedState {
  conversationId: string;
  messages: Message[];
}

export interface UseChatResult {
  messages: Message[];
  conversationId: string;
  isStreaming: boolean;
  send: (text: string, attachments?: Attachment[]) => Promise<void>;
  stop: () => void;
  reset: () => void;
  vote: (messageId: string, vote: FeedbackVote, comment?: string) => void;
}

export function useChat(options: UseChatOptions): UseChatResult {
  const {
    persist = true,
    storageKey = 'cbw',
    welcomeMessage,
    errorMessage = 'Something went wrong. Please try again.',
  } = options;

  const stateKey = `${storageKey}:state`;
  const config = useMemo(() => resolveConfig(options.config), [options.config]);

  // Callbacks live in a ref so a consumer's inline arrow functions do not
  // re-create `send` on every render.
  const handlers = useRef(options);
  handlers.current = options;

  const welcome = useMemo<Message[]>(
    () =>
      welcomeMessage
        ? [
            {
              id: 'welcome',
              role: 'assistant' as const,
              content: welcomeMessage,
              createdAt: Date.now(),
              status: 'done' as const,
            },
          ]
        : [],
    [welcomeMessage],
  );

  const [{ conversationId, messages }, setState] = useState<PersistedState>(() => {
    if (persist && typeof window !== 'undefined') {
      const saved = storage.get<PersistedState>(stateKey);
      if (saved?.conversationId && Array.isArray(saved.messages)) return saved;
    }
    return { conversationId: uid('c'), messages: welcome };
  });

  const [isStreaming, setIsStreaming] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const bufferRef = useRef('');
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---------------------------------------------------------------- *
   * Persistence
   * ---------------------------------------------------------------- */

  useEffect(() => {
    if (!persist) return;
    storage.set(stateKey, {
      conversationId,
      messages: messages.map((m) => ({
        ...m,
        status: m.status === 'streaming' || m.status === 'pending' ? 'done' : m.status,
        attachments: m.attachments?.map(serialiseAttachment),
      })),
    });
  }, [persist, stateKey, conversationId, messages]);

  useEffect(() => {
    handlers.current.onConversationChange?.(conversationId, messages);
  }, [conversationId, messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /* ---------------------------------------------------------------- *
   * Message helpers
   * ---------------------------------------------------------------- */

  const patch = useCallback((id: string, update: (message: Message) => Message) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) => (m.id === id ? update(m) : m)),
    }));
  }, []);

  const flushBuffer = useCallback(
    (id: string) => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      const chunk = bufferRef.current;
      if (!chunk) return;
      bufferRef.current = '';
      patch(id, (m) => ({ ...m, content: m.content + chunk, status: 'streaming' }));
    },
    [patch],
  );

  const scheduleFlush = useCallback(
    (id: string) => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        flushBuffer(id);
      }, FLUSH_INTERVAL_MS);
    },
    [flushBuffer],
  );

  /* ---------------------------------------------------------------- *
   * Attachments
   * ---------------------------------------------------------------- */

  const prepareAttachments = useCallback(
    async (attachments: Attachment[], signal: AbortSignal): Promise<Attachment[]> => {
      if (attachments.length === 0) return [];

      if (config.attachmentMode === 'base64') {
        return Promise.all(
          attachments.map(async (a) =>
            a.file ? { ...a, data: await fileToBase64(a.file), status: 'ready' as const } : a,
          ),
        );
      }

      if (config.attachmentMode === 'upload-endpoint') {
        if (!config.uploadUrl) {
          throw new Error("attachmentMode 'upload-endpoint' requires config.uploadUrl");
        }
        const headers = await resolveHeaders(config, { Accept: 'application/json' });
        return Promise.all(
          attachments.map(async (a) => {
            if (!a.file) return a;
            const form = new FormData();
            form.append(config.fileField, a.file, a.name);
            form.append('conversationId', conversationId);

            const response = await fetch(config.uploadUrl as string, {
              method: 'POST',
              headers,
              body: form,
              signal,
              credentials: config.credentials,
            });
            if (!response.ok) throw new Error(await describeHttpError(response));

            const result = (await response.json().catch(() => ({}))) as Record<string, unknown>;
            return {
              ...a,
              file: undefined,
              remoteId: (result.id ?? result.fileId ?? result.file_id) as string | undefined,
              url: (result.url ?? result.fileUrl) as string | undefined,
              status: 'ready' as const,
            };
          }),
        );
      }

      // 'multipart': files ride along with the chat request itself.
      return attachments;
    },
    [config, conversationId],
  );

  /* ---------------------------------------------------------------- *
   * Sending
   * ---------------------------------------------------------------- */

  const send = useCallback(
    async (text: string, attachments: Attachment[] = []) => {
      const trimmed = text.trim();
      if ((!trimmed && attachments.length === 0) || abortRef.current) return;

      const controller = new AbortController();
      abortRef.current = controller;
      const timeout = setTimeout(() => controller.abort(), config.timeoutMs);

      const userMessage: Message = {
        id: uid('u'),
        role: 'user',
        content: trimmed,
        createdAt: Date.now(),
        status: 'done',
        attachments: attachments.length ? attachments : undefined,
      };
      const assistantId = uid('a');
      const assistantMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        status: 'pending',
      };

      let history: Message[] = [];
      setState((prev) => {
        history = [...prev.messages, userMessage];
        return { ...prev, messages: [...history, assistantMessage] };
      });
      setIsStreaming(true);
      handlers.current.onSend?.(userMessage);

      let failed: Error | null = null;

      try {
        const prepared = await prepareAttachments(attachments, controller.signal);
        if (prepared !== attachments && prepared.length) {
          patch(userMessage.id, (m) => ({ ...m, attachments: prepared }));
        }

        const request: TransportRequest = {
          message: { ...userMessage, attachments: prepared },
          messages: [...history.slice(0, -1), { ...userMessage, attachments: prepared }],
          conversationId,
          attachments: prepared,
          signal: controller.signal,
          config,
        };

        const transport = resolveTransport(config);
        let sawError = false;

        // The authoritative copy of the reply. State updates are async, so the
        // final message is assembled here rather than read back out of state.
        let content = '';
        let meta: Record<string, unknown> | undefined;

        for await (const event of transport(request)) {
          if (controller.signal.aborted) break;

          switch (event.type) {
            case 'delta':
              content += event.text;
              bufferRef.current += event.text;
              scheduleFlush(assistantId);
              break;
            case 'message':
              content = event.text;
              bufferRef.current = '';
              if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
              }
              patch(assistantId, (m) => ({ ...m, content: event.text, status: 'streaming' }));
              break;
            case 'meta':
              meta = { ...meta, ...event.data };
              if (typeof event.data.conversationId === 'string') {
                const next = event.data.conversationId;
                setState((prev) => ({ ...prev, conversationId: next }));
              }
              patch(assistantId, (m) => ({ ...m, meta: { ...m.meta, ...event.data } }));
              break;
            case 'error':
              sawError = true;
              failed = new Error(event.error);
              break;
          }
          if (sawError) break;
        }

        flushBuffer(assistantId);

        if (failed) throw failed;

        const isEmpty = content.trim() === '';
        const finalMessage: Message = {
          ...assistantMessage,
          content,
          ...(meta ? { meta } : {}),
          status: isEmpty ? 'error' : 'done',
          ...(isEmpty ? { error: errorMessage } : {}),
        };
        patch(assistantId, () => finalMessage);
        if (!isEmpty) handlers.current.onResponse?.(finalMessage);
      } catch (err) {
        flushBuffer(assistantId);
        if (isAbortError(err)) {
          // Aborted turns keep whatever text arrived; empty ones disappear.
          setState((prev) => ({
            ...prev,
            messages: prev.messages.filter(
              (m) => !(m.id === assistantId && m.content.trim() === ''),
            ),
          }));
          patch(assistantId, (m) => ({ ...m, status: 'done' }));
        } else {
          const error = toError(err, errorMessage);
          patch(assistantId, (m) => ({ ...m, status: 'error', error: error.message }));
          handlers.current.onError?.(error);
        }
      } finally {
        clearTimeout(timeout);
        abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [config, conversationId, errorMessage, flushBuffer, patch, prepareAttachments, scheduleFlush],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    bufferRef.current = '';
    setState({ conversationId: uid('c'), messages: welcome });
    if (persist) storage.remove(stateKey);
  }, [persist, stateKey, welcome]);

  /* ---------------------------------------------------------------- *
   * Feedback
   * ---------------------------------------------------------------- */

  const vote = useCallback(
    (messageId: string, value: FeedbackVote, comment?: string) => {
      const current = messages.find((m) => m.id === messageId);
      if (!current) return;

      // Clicking the active vote again clears it.
      const nextVote = current.feedback === value && !comment ? null : value;
      const target: Message = { ...current, feedback: nextVote, feedbackComment: comment };

      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((m) => (m.id === messageId ? { ...m, ...target } : m)),
      }));

      if (nextVote === null) return;

      const payload: FeedbackPayload = {
        messageId,
        conversationId,
        vote: value,
        comment,
        message: target,
      };
      handlers.current.onFeedback?.(payload);

      if (config.feedbackUrl) {
        void (async () => {
          try {
            const headers = await resolveHeaders(config, { 'Content-Type': 'application/json' });
            await fetch(config.feedbackUrl as string, {
              method: 'POST',
              headers,
              credentials: config.credentials,
              body: JSON.stringify({
                messageId,
                conversationId,
                vote: value,
                comment: comment ?? null,
                content: target.content,
                createdAt: Date.now(),
              }),
            });
          } catch (err) {
            handlers.current.onError?.(toError(err, 'Failed to submit feedback'));
          }
        })();
      }
    },
    [config, conversationId, messages],
  );

  return { messages, conversationId, isStreaming, send, stop, reset, vote };
}
